import { useReactiveVar } from '@apollo/client';
import { getCurrentUser } from 'aws-amplify/auth';
import { clsx } from 'clsx';
import { useLocation, useNavigate } from 'react-router';
import { PrefetchPageLinks } from 'react-router';
import { PushNotificationSubscriptionInput } from '@/__generated__/graphql';
import { LayoutCard } from '@/components/layout/card';
import { sanitizeRedirectParam } from '@/routes-utils/sanitize-redirect-param';
import { pushService } from '@/services/push-service';
import { traceError } from '@/utils/trace-error';
import { useAddPushSubscription } from '../_app.p.settings.notifications_.$type/use-add-push-subscription';
import { ConfirmLoginForm } from './confirm-login-form';
import { LoginForm } from './login-form';
import { loginStepVar } from './login-step-var';
import { SetNewPasswordForm } from './set-new-password-form';
import { authCard, authContent, welcomeCard } from '../_auth/page.css';

export const LoginComponent = ({ navigateTo }: { navigateTo?: string }) => {
  const step = useReactiveVar(loginStepVar);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const fromParam = params.get('from');
  const to = sanitizeRedirectParam(fromParam, navigateTo);
  const navigate = useNavigate();
  const [addPushSubscription] = useAddPushSubscription();

  const handleLogin = async () => {
    const { userId } = await getCurrentUser();
    try {
      const subscription = await pushService.getSubscription();
      if (subscription) await subscription.unsubscribe();

      const pushSettings = pushService.getPushSettings();
      if (
        userId &&
        pushSettings[userId] &&
        'Notification' in window &&
        Notification.permission === 'granted'
      ) {
        const newSubscription = await pushService.subscribe();
        await addPushSubscription(
          newSubscription as PushNotificationSubscriptionInput,
        );
      }
    } catch (err) {
      traceError(
        new Error('Failed to subscribe on push notifications on login', {
          cause: err,
        }),
      );
    }
    navigate(to, { state: { afterAuth: true } });
  };

  return (
    <>
      <LayoutCard className={clsx(authCard, welcomeCard)}>
        <div className={authContent}>
          {step === 'login' && (
            <LoginForm
              setMfaStep={() => loginStepVar('mfa')}
              setNewPasswordStep={() => loginStepVar('newPassword')}
              onLogin={handleLogin}
            />
          )}
          {step === 'mfa' && <ConfirmLoginForm onLoginConfirm={handleLogin} />}
          {step === 'newPassword' && (
            <SetNewPasswordForm onNewPasswordSet={handleLogin} />
          )}
        </div>
      </LayoutCard>
      <PrefetchPageLinks page={to} />
    </>
  );
};
